import React, { useState } from "react";
import axios from "axios";
import { API, useApp } from "../state/AppContext";
import { Input } from "./ui/input";
import { Mail, Phone, User, Cake, GraduationCap, StickyNote, Download, CheckCircle2, CalendarClock, X, ArrowRight } from "lucide-react";

const GRADE_KEYS = [
  ["Pre-K", "grade_prek"],
  ["Kindergarten", "grade_k"],
  ["1st", "grade_1"],
  ["2nd", "grade_2"],
  ["3rd", "grade_3"],
  ["4th", "grade_4"],
  ["5th", "grade_5"],
  ["6th", "grade_6"],
  ["7th", "grade_7"],
  ["8th", "grade_8"],
];

/**
 * Native intake / enrollment form. Replaces the external Google Form flow
 * for guide downloads. The same form is used on both paths — the parent
 * screen passes `guide="parents"` (Kindness) or `guide="activity"` (Spinner).
 */
export default function IntakeForm({ guide, onDone, onClose, publicConfig = {} }) {
  const ctx = useApp();
  const { t, lang, sourcePath, utmSource, utmMedium, utmCampaign, utmContent } = ctx;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [age, setAge] = useState("");
  const [grade, setGrade] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { guide_url, guide_label }

  const isParents = guide === "parents";
  const kicker = t(isParents ? "intake_kicker_parents" : "intake_kicker_activity");
  const lead   = t(isParents ? "intake_lead_parents" : "intake_lead_activity");
  const submitLabel = t(isParents ? "intake_submit_parents" : "intake_submit_activity");

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/enrollments`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        child_age: age.trim(),
        grade: grade.trim(),
        notes: notes.trim(),
        path: sourcePath || (isParents ? "kindness" : "spinner"),
        requested_guide: isParents ? "parents" : "activity",
        language: lang || "en",
        utm_source: utmSource || "",
        utm_medium: utmMedium || "",
        utm_campaign: utmCampaign || "",
        utm_content: utmContent || "",
      });
      setResult(res.data);
      if (onDone) onDone(res.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg || "").filter(Boolean).join(", ") || t("intake_error") : t("intake_error");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const guideUrl = result.guide_url || (isParents ? publicConfig.parents_guide_url : publicConfig.activity_guide_url) || "";
    const rawTourUrl = publicConfig.tour_booking_url || "";
    const tourUrl = rawTourUrl && lang === "es"
      ? `${rawTourUrl}${rawTourUrl.includes("?") ? "&" : "?"}hl=es`
      : rawTourUrl;
    const logTourIntent = () => {
      // Fire & forget — never block the outbound tab
      axios.post(`${API}/tour-intents`, {
        family_name: name.trim(),
        family_email: email.trim(),
        language: lang || "en",
        source_path: sourcePath || (isParents ? "kindness" : "spinner"),
        utm_source: utmSource || "",
        utm_medium: utmMedium || "",
        utm_campaign: utmCampaign || "",
        utm_content: utmContent || "",
      }).catch(() => {});
    };
    return (
      <div className="p-6 text-center" data-testid="intake-success">
        <div className="mx-auto w-14 h-14 rounded-full bg-scl-teal/10 flex items-center justify-center mb-3">
          <CheckCircle2 className="w-7 h-7 text-scl-teal" />
        </div>
        <h3 className="font-serif text-2xl font-bold text-scl-charcoal mb-1">{t("intake_success_title")}</h3>
        <p className="text-scl-charcoal/70 text-sm leading-relaxed mb-5">{t("intake_success_body")}</p>
        {guideUrl && (
          <a
            href={guideUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            data-testid="intake-success-download"
            className="inline-flex items-center justify-center gap-2 w-full h-12 rounded-full bg-scl-coral text-white font-semibold hover:brightness-105 shadow-warm-sm mb-4"
          >
            <Download className="w-4 h-4" /> {t("intake_success_download")}
          </a>
        )}
        {tourUrl && (
          <a
            href={tourUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={logTourIntent}
            data-testid="intake-success-tour-card"
            className="block text-left bg-gradient-to-br from-scl-teal to-[#144e47] rounded-2xl p-4 shadow-warm mb-4 hover:brightness-110 transition-all active:scale-[0.99]"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0"><CalendarClock className="w-5 h-5 text-white" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-white/80 font-semibold">{t("intake_success_tour_kicker")}</div>
                <div className="font-serif text-white font-bold text-base leading-tight mt-0.5">{t("intake_success_tour_title")}</div>
                <div className="text-[11px] text-white/85 leading-snug mt-0.5">{t("intake_success_tour_body")}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-white/85 shrink-0" />
            </div>
          </a>
        )}
        {onClose && (
          <button onClick={onClose} className="text-xs text-scl-charcoal/50 hover:text-scl-charcoal underline underline-offset-2" data-testid="intake-success-close">
            {t("intake_success_close")}
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="p-5 relative" data-testid="intake-form">
      {onClose && (
        <button type="button" onClick={onClose} aria-label="close" className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-scl-cream flex items-center justify-center text-scl-charcoal/50 hover:text-scl-charcoal">
          <X className="w-4 h-4" />
        </button>
      )}
      <div className="text-[10px] uppercase tracking-[0.18em] text-scl-teal font-semibold mb-1">{kicker}</div>
      <h3 className="font-serif text-xl text-scl-charcoal font-bold leading-snug mb-2">{isParents ? t("home_parents_cta") : t("intake_kicker_activity")}</h3>
      <p className="text-sm text-scl-charcoal/70 mb-4 leading-relaxed">{lead}</p>

      <Field icon={User} label={t("intake_name")} required>
        <Input data-testid="intake-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("intake_name_ph")} className="h-11 rounded-xl border-scl-border bg-white" required />
      </Field>
      <Field icon={Mail} label={t("intake_email")} required>
        <Input data-testid="intake-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 rounded-xl border-scl-border bg-white" required />
      </Field>
      <Field icon={Phone} label={t("intake_phone")} hint={t("optional")}>
        <Input data-testid="intake-phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("intake_phone_ph")} className="h-11 rounded-xl border-scl-border bg-white" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field icon={Cake} label={t("intake_age")} hint={t("optional")}>
          <Input data-testid="intake-age" type="text" inputMode="numeric" value={age} onChange={(e) => setAge(e.target.value.replace(/[^0-9]/g, "").slice(0,2))} placeholder={t("intake_age_ph")} className="h-11 rounded-xl border-scl-border bg-white" />
        </Field>
        <Field icon={GraduationCap} label={t("intake_grade")} hint={t("optional")}>
          <select data-testid="intake-grade" value={grade} onChange={(e) => setGrade(e.target.value)} className="h-11 w-full rounded-xl border border-scl-border bg-white px-3 text-sm text-scl-charcoal focus:outline-none focus:ring-2 focus:ring-scl-teal/30">
            <option value="">{t("intake_grade_ph")}</option>
            {GRADE_KEYS.map(([v, key]) => <option key={v} value={v}>{t(key)}</option>)}
          </select>
        </Field>
      </div>
      <Field icon={StickyNote} label={t("intake_notes")} hint={t("intake_notes_help")}>
        <textarea data-testid="intake-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full rounded-xl border border-scl-border bg-white px-3 py-2 text-sm text-scl-charcoal focus:outline-none focus:ring-2 focus:ring-scl-teal/30 resize-none" />
      </Field>

      {error && <div data-testid="intake-error" className="text-xs text-scl-brick mb-3 bg-scl-brick/10 rounded-lg px-3 py-2">{error}</div>}

      <button
        type="submit"
        disabled={submitting}
        data-testid="intake-submit"
        className="w-full h-12 rounded-full bg-scl-coral text-white font-semibold text-[15px] shadow-warm-sm hover:brightness-105 active:scale-[0.98] transition-all disabled:opacity-70 flex items-center justify-center gap-2 min-h-[44px]"
      >
        <Download className="w-4 h-4" />
        {submitting ? t("intake_sending") : submitLabel}
      </button>
      <p className="mt-3 text-[11px] text-scl-charcoal/50 leading-relaxed">{t("intake_privacy")}</p>
    </form>
  );
}

function Field({ icon: Icon, label, required = false, hint = "", children }) {
  return (
    <div className="mb-3">
      <label className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold mb-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span>{label}</span>
        {required && <span className="text-scl-brick normal-case tracking-normal">*</span>}
        {!required && hint && <span className="text-scl-charcoal/40 normal-case tracking-normal">— {hint}</span>}
      </label>
      {children}
    </div>
  );
}
